<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
  >
    <template v-slot:header>
      <v-card-title class="screen-title">
        イントラネットFW プラットフォームゲートウェイ ポリシー設定
      </v-card-title>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click="savePolicy"
      >
        ×
      </button>
    </template>
    <template v-slot:body>
      <v-card-text class="pa-0">
        <div class="modal-body">
          <div class="font-weight-bold mt-3">
            ゾーン一覧画面で、反映ボタンを押さないと機器には反映されません
          </div>
          {{ zoneRelationToDisplay }}
          <v-container fluid class="pa-1">
            <v-row
              no-gutters
              justify="space-between"
              class="my-2"
              align="center"
            >
              <span>{{ items.length }}件を表示</span>
              <v-btn
                color="primary"
                v-permission.config
                @click="showPolicyRuleEdit"
              >
                ポリシー編集
              </v-btn>
            </v-row>
            <app-table
              density="compact"
              :fields="fields"
              :items="items"
              :per-page="-1"
              primary-key="policyId"
              :td-class="tdClass"
              :tr-class="policyRowClass"
            >
              <!-- 送信元アドレス -->
              <template #cell(srcAddress)="{ item }">
                <intranet-policy-src-address
                  :ip-address-list="item.srcAddressList"
                />
              </template>

              <!-- 宛先アドレス -->
              <template #cell(dstAddress)="{ item }">
                <intranet-policy-dst-address
                  :ip-address-list="item.dstAddressList"
                />
              </template>

              <!-- サービス -->
              <template #cell(service)="{ item }">
                <intranet-policy-service
                  :custom-service-list="item.customServiceList"
                  :default-service-list="item.serviceList"
                />
              </template>

              <!-- アクション -->
              <template #cell(actionType)="{ value }">
                {{ $filter.enumTo(value, "allow_deny") }}
              </template>

              <!-- 有効/有無 -->
              <template #cell(isPolicyStatus)="{ value }">
                {{ $filter.enumTo(value, "enable") }}
              </template>

              <!-- 備考 -->
              <template #cell(description)="{ value }">
                {{ $filter.emptyTo(value, "-") }}
              </template>
            </app-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn variant="outlined" color="primary" @click="savePolicy">
              戻る
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { IntranetFwPolicy, IntraPolicy } from "@/apis/IntranetfwPolicyApi";
import IntranetPolicySrcAddress from "@/components/policy/IntranetPolicySrcAddress.vue";
import IntranetPolicyDstAddress from "@/components/policy/IntranetPolicyDstAddress.vue";
import IntranetPolicyService from "@/components/policy/IntranetPolicyService.vue";
import cloneDeep from "lodash/cloneDeep";
import type { Pfgw } from "@/apis/IntranetfwServiceMenuApi";
import type { PfgwZoneType } from "@/modals/applianceContractSetting/IntranetFirewall/policy/pfgw/IntranetFwPfgwPolicyZoneList.vue";
import { VueFinalModal } from "vue-final-modal";
import type { AppTableHeader } from "@/components/AppTable.vue";
import IntranetFwPfgwPolicyRuleViewRD from "@/modals/applianceContractSetting/IntranetFirewall/policy/pfgw/IntranetFwPfgwPolicyRuleViewRD.vue";

export default defineComponent({
  name: "IntranetFwPfgwPolicyList",
  props: {
    /** イントラネットFWポリシー */
    policyList: {
      type: Array as PropType<IntraPolicy[]>,
      required: true,
    },
    /** イントラネットFWポリシー */
    intranetFwPolicy: {
      type: Object as PropType<IntranetFwPolicy>,
      required: true,
    },
    /** 送信元ゾーン */
    srcZone: {
      type: String as PropType<PfgwZoneType>,
      required: true,
    },
    /** PFGW */
    pfgw: {
      type: Object as PropType<Pfgw>,
      required: true,
    },
  },
  components: {
    IntranetPolicySrcAddress,
    IntranetPolicyDstAddress,
    IntranetPolicyService,
  },
  data() {
    return {
      fields: [
        { key: "policyId", title: "ID", width: "80px" },
        { key: "srcAddress", title: "送信元アドレス", width: "150px" },
        { key: "dstAddress", title: "宛先アドレス", width: "150px" },
        { key: "service", title: "サービス", width: "15%" },
        { key: "actionType", title: "アクション", width: "80px" },
        { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
        { key: "description", title: "備考", width: "200px" },
      ],
      items: cloneDeep(this.policyList) as IntraPolicy[],
    };
  },
  computed: {
    /** ゾーン接続方向の表示情報 */
    zoneRelationToDisplay(): string {
      return `${this.srcZone} → ${this.srcZone === "WVS2" ? "KCPS" : "WVS2"}`;
    },
  },
  methods: {
    tdClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
    /**
     * ポリシーテーブルのtrクラス.
     * 編集不可デフォルトポリシーの場合は特定のクラスを返す
     */
    policyRowClass(
      item: Pick<IntraPolicy, "policyCategoryInfo">
    ): string[] | undefined {
      return item.policyCategoryInfo === "UNEDITABLE_DEFAULT_POLICY"
        ? ["gray-out"]
        : undefined;
    },
    /** ポリシー編集モーダルを表示 */
    async showPolicyRuleEdit() {
      // ポリシー編集モーダルの表示 ポリシー編集で変更した内容を受け取る
      this.items = await this.$modal.show(IntranetFwPfgwPolicyRuleViewRD, {
        srcZone: this.srcZone,
        policyList: this.items,
        intranetFwPolicy: this.intranetFwPolicy,
        pfgw: this.pfgw,
      });
    },
    /**
     * 戻るボタン・×を押下した時の処理
     * モーダルを閉じて遷移元に編集したポリシーを返却する
     */
    savePolicy() {
      (
        this.$refs.modal as typeof VueFinalModal & { ok: (e: unknown) => void }
      ).ok(this.items);
    },
  },
});
</script>
<style lang="scss" scoped>
:deep(th) {
  font-size: 90%;
}
</style>
